/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';

export type OrdinalAccessor<T> = (object: T) => number;

export interface NewOrdinalAssignment<T> {
	object: T;
	ordinal: number;
}

export enum ResultStatus {
	REQUIRES_FULL_RENUMBERING = 0,
}

/**
 * An "ordinal" is defined as "a number defining a thing's position in a series, such as 'first', 'second', ..."
 * "Ordinal" is a more concise term for "order number" or "ordering value".
 * Ordinal numbers are floating point, and thus need not be whole integers.
 * They also can be zero or negative.
 *
 * The purpose of this class is to help maintain arrays of objects of the generic type, T, which include an
 * ordinal number, and where the array elements are sorted (in ascending order) by each T object's ordinal.
 * For example, an object may have a nested field at "view.ordinal"; such a sorted array might look like:
 *
 *     [ { name: 'A', view.ordinal: 100 }, { name: 'B', view.ordinal: 200 }, { name: 'C', view.ordinal: 300 } ]
 *
 * The sample objects below are named.  Say the UI wanted to reorder them so that 'C' was second.
 * A valid reordering could be any of the following:
 *
 *     // Most obvious; only renumbers 'C'
 *     [ { 'A', 100 }, { 'C', 150 }, { 'B', 200 } ]
 *
 *     // The number doesn't HAVE to be exactly in the middle
 *     [ { 'A', 100 }, { 'C', 101 }, { 'B', 200 } ]
 *
 *     // There's nothing that says other elements can't be renumbered
 *     [ { 'A', 100 }, { 'C', 200 }, { 'B', 300 } ]
 *
 *     // Floating point and negative values are allowed too
 *     [ { 'A', -100 }, { 'C', 0.001 }, { 'B', 200 } ]
 *
 *     // This is a more realistic floating point example:
 *     [ { 'A', 10 }, { 'C', 10.5 }, { 'B', 11 } ]
 *
 * Obviously we'd like to avoid renumbering more than moved objects (as in the third example), but it is an option.
 *
 * This class doesn't actually modify any sorted lists or their elements' ordinal values.
 * That is left up to the caller to decide how to do (i.e. directly to in-memory object arrays,
 * indirectly by changing the underlying database model, etc.).  The NewOrdinalAssignments calculated by this class,
 * however, provide the information about how any ordinal values should be assigned to objects in order to maintain
 * sorting.
 */
export class OrdinalCalculator<T> {
	public static MINIMUM_ORDINAL_DELTA = 1;

	private defaultOrderDelta: number;
	private ordinalAccessor: OrdinalAccessor<T>;
	private allowFloatingPoint: boolean;

	/**
	 * @param ordinalAccessor Function for getting at the ordinal value inside a T object
	 * @param number The default difference between ordinals of successive elements in a T array
	 */
	constructor(ordinalAccessor: OrdinalAccessor<T>, allowFloatingPoint: boolean, defaultOrderDelta: number) {
		this.defaultOrderDelta = defaultOrderDelta;
		this.allowFloatingPoint = allowFloatingPoint;
		this.ordinalAccessor = ordinalAccessor;
	}

	private ordinalAt(list: T[], index: number): number {
		return this.ordinalAccessor(list[index]);
	}

	/**
	 * Returns true if every element in the given array has a valid ordinal number,
	 * has a minimum ordinal "distance" from its neighbor (MINIMUM_ORDINAL_DELTA),
	 * is an integer if (!this.allowFloatingPoint)
	 * and if the elements are sorted numerically in ascending order;
	 * returns false otherwise.
	 *
	 * @param list The list to validate
	 * @param allowAdjacentDuplicates If true, then return true if two adjacent elements have equal ordinal values
	 */
	public ordinalsAreValidMinimallySpacedAndSorted(list: T[], allowAdjacentDuplicates = true): boolean {
		let first = true;
		let prevOrdinal: number;
		for (const elem of list) {
			const nextOrdinal = this.ordinalAccessor(elem);
			if (nextOrdinal !== 0 && !nextOrdinal) {
				return false;
			}
			if (!this.allowFloatingPoint && !Number.isInteger(nextOrdinal)) {
				return false;
			}
			if (first) {
				first = false;
			} else {
				if (prevOrdinal > nextOrdinal) {
					// Properly sorted?
					return false;
				}
				if (prevOrdinal === nextOrdinal) {
					if (!allowAdjacentDuplicates) {
						return false;
					}
				} else {
					if (nextOrdinal - prevOrdinal < OrdinalCalculator.MINIMUM_ORDINAL_DELTA) {
						// Spaced enough apart?
						return false;
					}
				}
			}
			prevOrdinal = nextOrdinal;
		}
		return true;
	}

	/**
	 * <p>
	 * Given one or more "whatToInsert" objects that need to be inserted into the given sorted "destList" at the specified
	 * "destIndex", this method returns the "NewOrdinalAssignment"s needed for that insertion, and possibly to update ordinals
	 * of other elements in "destList".
	 * </p>
	 * <p>
	 * This method doesn't actually perform any insertion; that is left up to the caller to decide how to do (i.e. directly
	 * in "destList", indirectly by changing the underlying database model, etc.).  The returned NewOrdinalAssignments,
	 * however, provide the information about how any such insertion should assign new ordinal values to the objects in
	 * "whatToInsert".
	 * </p>
	 * <p>
	 * REQUIREMENTS:
	 * (1) This method requires "areOrdinalsAllValidAndSorted(destList, true)" returns true, meaning "destList" has valid ordinals
	 * and has sorted elements.<br>
	 * (2) This method requires that the objects in "whatToInsert" are not in "destList".
	 * </p>
	 *
	 * @param destList The sorted listed of objects into which the "whatToInsert" objects want to be inserted
	 * @param whatToInsert The objects that want to be inserted at "destIndex" and which need new ordinals for that location
	 * @param destIndex The destination index
	 * @return the "NewOrdinalAssignment"s needed for that insertion or 'REQUIRES_FULL_RENUMBERING' if the insertion would
	 * introduce floating point ordinal values and this OrdinalCalculator's "allowFloatingPoint" property is false
	 */
	public calcNewOrdinalsForInsert(
		destList: T[],
		whatToInsert: T | T[],
		destIndex: number
	): Array<NewOrdinalAssignment<T>> | ResultStatus.REQUIRES_FULL_RENUMBERING {
		const movingObjects: T[] = Array.isArray(whatToInsert) ? whatToInsert : [whatToInsert];

		// Determine the existing ordinals before and after the destination location
		// and name those "startOrdinal" and "endOrdinal".  Handle various others cases,
		// like prepending, appending and starting with an empty "currentList".
		let startOrdinal: number;
		let endOrdinal: number;

		// Just for the sake of sample comments, imagine defaultOrderDelta is 100
		if (destList.length === 0) {
			//     MOVING INTO AN EMPTY LIST:
			// [ startOrder .. endOrder ] = [ 0 .. (movingObjects.length * 100) ]
			startOrdinal = 0;
			endOrdinal = (movingObjects.length + 1) * this.defaultOrderDelta;
		} else if (destIndex === 0) {
			//     PREPENDING:
			// firstOrdinal = list[0].ordinal
			// [ startOrder .. endOrder ] = [ (firstOrdinal - movingObjects.length * 100) .. firstOrdinal ]
			endOrdinal = this.ordinalAt(destList, 0);
			startOrdinal = endOrdinal - (movingObjects.length + 1) * this.defaultOrderDelta;
		} else if (destIndex >= destList.length) {
			//     APPENDING:
			// lastOrdinal = list[list.length - 1].ordinal
			// [ startOrder .. endOrder ] = [ lastOrdinal .. (lastOrdinal + 100) ]
			startOrdinal = this.ordinalAt(destList, destList.length - 1);
			endOrdinal = startOrdinal + (movingObjects.length + 1) * this.defaultOrderDelta;
		} else {
			//     INSERTING:
			// [ startOrder .. endOrder ] = [ Ordinal at (destIndex-1) .. Ordinal at destIndex ]
			startOrdinal = this.ordinalAt(destList, destIndex - 1);
			endOrdinal = this.ordinalAt(destList, destIndex);
		}

		// Next...
		// Go through the objects that need to be moved and give them new ordinals
		// which fall between "startOrdinal" and "endOrdinal":
		const offset = (endOrdinal - startOrdinal) / (movingObjects.length + 1);

		if (!this.allowFloatingPoint && !Number.isInteger(offset)) {
			return ResultStatus.REQUIRES_FULL_RENUMBERING;
		}

		const result: Array<NewOrdinalAssignment<T>> = [];
		movingObjects.forEach((obj, idx) => {
			result.push({
				object: obj,
				ordinal: startOrdinal + (idx + 1) * offset,
			});
		});
		return result;
	}

	/**
	 * <p>
	 * Given "moveCount" number of objects that need to be moved the given sorted "destList", from the specified
	 * "sourceIndex", this method returns the "NewOrdinalAssignment"s needed for that move,
	 * and possibly to update ordinals of other elements in "destList".
	 * This method doesn't actually perform any move; that is left up to the caller to decide how to do (i.e. directly
	 * in "destList", indirectly by changing the underlying database model, etc.).  The returned NewOrdinalAssignments,
	 * however, provide the information about how any such a move should assign new ordinal values to the objects being moved.
	 * </p>
	 * <p>
	 * REQUIREMENTS:
	 * This method requires "areOrdinalsAllValidAndSorted(destList, true)" returns true, meaning "destList" has valid ordinals
	 * and has sorted elements.
	 * </p>
	 *
	 * @param destList The sorted listed of objects which contains elements we want to move, and those elements need new ordinals
	 * @param sourceIndex The index of the first element we want to move
	 * @param moveCount The number of elements we want to move
	 * @param destIndex When the elements are removed for the move, "desList" becomes shorter; this "destIndex" is the index in
	 *     that shorter list at which to insert the elements to move.
	 * @returns "null" if any indices or the "moveCount" are invalid; otherwise, the NewOrdinalAssignments with the information
	 *     needed to adjust the ordinals of the elements to move
	 */
	public calcNewOrdinalsForMove(
		destList: T[],
		sourceIndex: number,
		moveCount: number,
		destIndex: number
	): Array<NewOrdinalAssignment<T>> {
		// Bounds checks...
		if (moveCount === 0) {
			return [];
		}
		const len = destList.length;
		const lastSourceIndex = sourceIndex + moveCount - 1;
		if (
			moveCount < 0 ||
			sourceIndex < 0 ||
			lastSourceIndex >= len ||
			destIndex < 0 ||
			destIndex > len - moveCount
		) {
			return null;
		}

		// Internally in this method, we convert this to a case of "calcNewOrdinalsForInsert".

		// First, make a working copy array of "destList":
		const workingList = [...destList];

		// Get the elements to insert:
		const whatToInsert = workingList.splice(sourceIndex, moveCount);

		const insertionResult = this.calcNewOrdinalsForInsert(workingList, whatToInsert, destIndex);
		if (insertionResult !== ResultStatus.REQUIRES_FULL_RENUMBERING) {
			return insertionResult;
		}

		// Full renumbering is required:
		workingList.splice(destIndex, 0, ...whatToInsert);
		return this.renumberList(workingList);
	}

	/**
	 * Returns an new ordinal value that can be used to prepend a new T object to the given "destList",
	 * so that - if prepended with that value - the "destList" will maintain its valid and sorted ordinals.
	 * This method requires "areOrdinalsAllValidAndSorted(destList, true)" returns true, meaning "destList" has valid ordinals
	 * and has sorted elements.
	 *
	 * @param destList The list to get new ordinal for an object to prepend to the list
	 */
	public newOrdinalToPrepend(destList: T[]): number {
		if (destList.length === 0) {
			return 0;
		}
		return this.ordinalAt(destList, 0) - this.defaultOrderDelta;
	}

	/**
	 * Returns an new ordinal value that can be used to append a new T object to the given "destList",
	 * so that - if appended with that value - the "destList" will maintain its valid and sorted ordinals.
	 * This method requires "areOrdinalsAllValidAndSorted(destList, true)" returns true, meaning "destList" has valid ordinals
	 * and has sorted elements.
	 *
	 * @param destList The list to get new ordinal for an object to append to the list
	 */
	public newOrdinalToAppend(destList: T[]): number {
		if (destList.length === 0) {
			return 0;
		}
		return this.ordinalAt(destList, destList.length - 1) + this.defaultOrderDelta;
	}

	/**
	 * Returns an array of NewOrdinalAssignment's for the given list such that if the new assignments
	 * are applied to the list's objects' ordinals, the list will be renumbered using this calculator's
	 * "defaultOrderDelta", sorted in ascending order and with the new ordinals centered around zero.
	 *
	 * @param list The list to get new ordinal assignments for
	 */
	public renumberList(list: T[]): Array<NewOrdinalAssignment<T>> {
		// Subtracting 1 next is for symmetry; take my word for it...
		let currentOrdinal = -1 * ((list.length - 1) / 2) * this.defaultOrderDelta;
		const result = new Array<NewOrdinalAssignment<T>>(list.length);
		list.forEach(function (obj, idx) {
			result[idx] = {
				object: obj,
				ordinal: currentOrdinal,
			};
			currentOrdinal += this.defaultOrderDelta;
		}, this);
		return result;
	}

	/**
	 * Checks to see whether the given "list"s ordinals are all valid
	 * and in strictly ascending order (that is, no adjacent duplicates).
	 * If so, null is returned.
	 * If not, new assignments are returned from "renumberList" which, if applied
	 * to the list, will make the list valid and sorted in ascending order.
	 */
	public renumberListIfNecessary(list: T[]): Array<NewOrdinalAssignment<T>> {
		if (this.ordinalsAreValidMinimallySpacedAndSorted(list, false)) {
			return null;
		}
		return this.renumberList(list);
	}
}

@Injectable({ providedIn: 'root' })
export class OrdinalCalculatorFactory {
	constructor() {}

	/**
	 * Not currently used, but here in case we decide to lose our sanity and re-embrace the satanic Floating Point
	 *
	 * @param ordinalAccessor Function for getting at the ordinal value inside a T object
	 * @param defaultOrderDelta Optional default difference between ordinals of successive elements in a T array
	 */
	public newFloatingPointCalculator<T>(
		ordinalFieldAccessor: OrdinalAccessor<T>, // E.g. (obj: T) => { return (obj as any).data['userOrder']; }
		defaultOrderDelta?: number
	) {
		return new OrdinalCalculator<T>(
			ordinalFieldAccessor,
			true, // allowFloatingPoint,
			defaultOrderDelta || 100
		);
	}

	/**
	 * Returns a new OrdinalCalcular that only produces blessed Integers
	 *
	 * @param ordinalAccessor Function for getting at the ordinal value inside a T object
	 */
	public newIntegerCalculator<T>(
		ordinalFieldAccessor: OrdinalAccessor<T> // E.g. (obj: T) => { return (obj as any).data['userOrder']; }
	) {
		return new OrdinalCalculator<T>(
			ordinalFieldAccessor,
			false, // allowFloatingPoint
			128 // defaultOrderDelta
		);
	}
}
